import { useEffect, useState, useCallback } from "react";
import { api, fmtNum } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import QualityScore from "@/components/QualityScore";
import ExportStudio from "@/components/ExportStudio";
import VersionVault from "@/components/VersionVault";
import ImproveDialog from "@/components/ImproveDialog";
import { Database, FileText, Download, Eye, Globe, Coins, Layers, Wand2 } from "lucide-react";
import { cn } from "@/lib/utils";

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-lg border border-border bg-[#0B0F18] px-4 py-4">
      <div className="flex items-center gap-2 text-slate-500">
        <Icon className={cn("h-4 w-4", accent)} />
        <span className="text-[11px] font-mono uppercase tracking-wider">{label}</span>
      </div>
      <p className="font-mono font-black text-2xl mt-1.5 text-white tabular-nums">{value}</p>
    </div>
  );
}

const EXPORTS = [];

export default function DatasetStudio({ activeCrawl }) {
  const [pages, setPages] = useState([]);
  const [stats, setStats] = useState({ total: 0, tokens: 0, domains: 0 });
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [quality, setQuality] = useState(null);
  const [history, setHistory] = useState(null);
  const [qLoading, setQLoading] = useState(false);
  const [improveOpen, setImproveOpen] = useState(false);

  const loadQuality = useCallback(async (refresh = false) => {
    if (!activeCrawl) { setQuality(null); setHistory(null); return; }
    setQLoading(true);
    try {
      setQuality(await api.getQuality(activeCrawl.id, refresh));
      setHistory(await api.getQualityHistory(activeCrawl.id));
    } catch (e) {
      setQuality(null);
    } finally { setQLoading(false); }
  }, [activeCrawl]);

  const load = useCallback(async () => {
    if (!activeCrawl) { setPages([]); setStats({ total: 0, tokens: 0, domains: 0 }); return; }
    setLoading(true);
    try {
      const data = await api.getPages(activeCrawl.id, 0, 100);
      setPages(data.pages);
      setStats(data.stats);
    } finally { setLoading(false); }
  }, [activeCrawl]);

  useEffect(() => { load(); loadQuality(); }, [load, loadQuality]);

  const handleExport = (fmt) => {
    if (!stats.total) { toast.error("No data to export yet"); return; }
    window.open(api.exportUrl(activeCrawl.id, fmt), "_blank");
    toast.success(`Exporting ${fmt.toUpperCase()}Ã¢â‚¬Â¦`);
  };

  if (!activeCrawl) {
    return (
      <div className="p-8 h-screen grid place-items-center">
        <div className="text-center text-slate-500">
          <Database className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p className="font-mono text-sm">Select or create a crawl job to view its dataset.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-[1600px]">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-mono font-extrabold text-2xl sm:text-3xl tracking-tight uppercase">Dataset Studio</h2>
          <p className="text-sm text-slate-500 mt-1">Job: <span className="text-slate-300 font-mono">{activeCrawl.name}</span></p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="improve-open-button" onClick={() => setImproveOpen(true)}
            className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold">
            <Wand2 className="h-4 w-4 mr-1.5" /> Improve This Corpus
          </Button>
          <Button variant="outline" onClick={() => { load(); loadQuality(true); }} data-testid="dataset-refresh-button" className="border-border hover:bg-slate-800">
            Refresh
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={FileText} label="Total Pages" value={fmtNum(stats.total)} accent="text-cyan-400" />
        <StatCard icon={Coins} label="Est. Tokens" value={fmtNum(stats.tokens)} accent="text-amber-400" />
        <StatCard icon={Globe} label="Unique Domains" value={fmtNum(stats.domains)} accent="text-emerald-400" />
        <StatCard icon={Layers} label="Status" value={activeCrawl.status} accent="text-purple-400" />
      </div>

      <QualityScore report={quality} history={history} loading={qLoading} onRefresh={() => loadQuality(true)} />

      <ExportStudio crawlId={activeCrawl.id} hasPages={stats.total > 0} onSaved={() => toast.info("Find it in the Files tab")} />

      <VersionVault crawlId={activeCrawl.id} onChanged={() => { load(); loadQuality(true); }} />

      <ImproveDialog crawlId={activeCrawl.id} open={improveOpen} onOpenChange={setImproveOpen}
        onApplied={() => { load(); loadQuality(true); }} />

      {/* Table */}
      <div className="rounded-lg border border-border bg-[#0B0F18] overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="font-mono text-[11px] uppercase text-slate-500">Title</TableHead>
                <TableHead className="font-mono text-[11px] uppercase text-slate-500">Domain</TableHead>
                <TableHead className="font-mono text-[11px] uppercase text-slate-500 text-right">Tokens</TableHead>
                <TableHead className="font-mono text-[11px] uppercase text-slate-500 text-right">Relevance</TableHead>
                <TableHead className="font-mono text-[11px] uppercase text-slate-500 text-right">AI Quality</TableHead>
                <TableHead className="font-mono text-[11px] uppercase text-slate-500 text-right">View</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="dataset-table-body">
              {pages.length === 0 && (
                <TableRow className="border-border">
                  <TableCell colSpan={6} className="text-center text-slate-600 font-mono text-sm py-10">
                    {loading ? "LoadingÃ¢â‚¬Â¦" : "No pages collected yet Ã¢â‚¬â€ start the crawl in Control Room."}
                  </TableCell>
                </TableRow>
              )}
              {pages.map((p) => (
                <TableRow key={p.id} className="border-border hover:bg-slate-800/40">
                  <TableCell className="max-w-[360px]">
                    <p className="truncate text-sm text-slate-200">{p.title}</p>
                    <p className="truncate text-[11px] text-slate-600 font-mono">{p.url}</p>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-slate-400">{p.domain}</TableCell>
                  <TableCell className="font-mono text-xs text-right text-amber-400">{fmtNum(p.tokens_est)}</TableCell>
                  <TableCell className="text-right">
                    <Badge variant="outline" className="font-mono text-[10px] border-cyan-500/40 text-cyan-300">
                      {p.relevance_score}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {p.llm_quality_score != null ? (
                      <Badge variant="outline" className={`font-mono text-[10px] ${p.llm_quality_score >= 0.7 ? "border-emerald-500/40 text-emerald-300" : p.llm_quality_score >= 0.4 ? "border-amber-500/40 text-amber-300" : "border-rose-500/40 text-rose-300"}`}>
                        {p.llm_quality_score.toFixed(2)}
                      </Badge>
                    ) : (
                      <span className="text-slate-700 text-xs">--</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" data-testid={`view-page-${p.id}`}
                      onClick={() => setSelected(p)} className="h-7 text-slate-400 hover:text-cyan-300">
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl bg-[#0B0F18] border-border max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="font-display truncate pr-6">{selected?.title}</DialogTitle>
            <DialogDescription asChild>
              <a href={selected?.url} target="_blank" rel="noreferrer" className="text-xs font-mono text-cyan-400 hover:underline truncate">{selected?.url}</a>
            </DialogDescription>
          </DialogHeader>
          <Tabs defaultValue="text" className="min-h-0">
            <TabsList className="bg-[#0E1420]">
              <TabsTrigger value="text" data-testid="page-text-tab">Content</TabsTrigger>
              <TabsTrigger value="meta" data-testid="page-meta-tab">Metadata</TabsTrigger>
            </TabsList>
            <TabsContent value="text">
              <pre className="terminal-scroll whitespace-pre-wrap text-xs text-slate-300 bg-[#04060A] rounded-md p-4 max-h-[55vh] overflow-y-auto font-mono">
                {selected?.content}
              </pre>
            </TabsContent>
            <TabsContent value="meta">
              <pre className="terminal-scroll text-xs text-cyan-300 bg-[#04060A] rounded-md p-4 max-h-[55vh] overflow-y-auto font-mono">
                {JSON.stringify({ url: selected?.url, domain: selected?.domain, status_code: selected?.status_code, tokens_est: selected?.tokens_est, relevance_score: selected?.relevance_score, llm_quality_score: selected?.llm_quality_score, llm_labels: selected?.llm_labels, extraction_mode: selected?.extraction_mode, metadata: selected?.metadata, links: selected?.links }, null, 2)}
              </pre>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
}
